<script lang="ts">
  import AppearanceSettings from './AppearanceSettings.svelte';
  import LicensesDialog from './LicensesDialog.svelte';
  import Button from '../ui/Button.svelte';
  import Card from '../ui/Card.svelte';
  import Select from '../ui/Select.svelte';
  import ThemeIcon from '../themes/ThemeIcon.svelte';
  import Disclosure from '../ui/Disclosure.svelte';
  import { MODE_INFO } from '../content/modes';
  import { POWER_UP_INFO } from '../content/power-ups';
  import { VARIANT_TITLES, TUTORIAL_PAGES } from '../content/tutorial';
  import FeedbackPanel from './FeedbackPanel.svelte';
  import { AVATARS, GAME_VARIANTS, MAX_NAME_LENGTH, type Avatar } from '../../shared/config';
  interface Props {
    name: string;
    avatar: Avatar;
    getProfileCredential: () => Promise<string>;
    saveName: (value: string) => void;
    saveAvatar: (value: Avatar) => void;
    clearUserData: () => void;
  }
  let { name, avatar, getProfileCredential, saveName, saveAvatar, clearUserData }: Props = $props();

  let licensesOpen = $state(false);

  const latteUrl = (() => {
    const value = import.meta.env.VITE_LATTE_URL;
    try {
      return value && new URL(value).protocol === 'https:' ? value : '';
    } catch {
      return '';
    }
  })();
</script>

<section id="screen-settings" class="screen submenu">
  <Card>
    <label for="settings-name">Name</label><input
      id="settings-name"
      value={name}
      maxlength={MAX_NAME_LENGTH}
      autocomplete="nickname"
      required
      onchange={(event) => {
        const value = event.currentTarget.value.trim().slice(0, MAX_NAME_LENGTH) || name;
        event.currentTarget.value = value;
        saveName(value);
      }}
    />
    <label for="settings-avatar">Avatar</label>
    <Select
      id="settings-avatar"
      value={avatar}
      onchange={saveAvatar}
      options={AVATARS.map((value) => ({ value, label: value }))}
    />
    <AppearanceSettings />
  </Card>
  <Card as="section" class="settings-actions" aria-label="Help and support">
    <Disclosure id="settings-rules" label="Rules" icon="rules" full>
      <h2>How to play</h2>
      {#each GAME_VARIANTS as variant (variant)}
        <h3>{VARIANT_TITLES[variant]}</h3>
        {#each TUTORIAL_PAGES[variant] as page (page.title)}
          <p><strong>{page.title}.</strong> {page.text}</p>
          {#each page.powerUps ?? [] as kind (kind)}
            <p><strong>{POWER_UP_INFO[kind].title}:</strong> {POWER_UP_INFO[kind].description}</p>
          {/each}
        {/each}
      {/each}
      <h2>Modes</h2>
      {#each Object.values(MODE_INFO) as mode (mode.title)}
        <h3>{mode.title}</h3>
        <p>{mode.rules}</p>
      {/each}
    </Disclosure>
    <Button variant="ghost" full onclick={() => (licensesOpen = true)}>
      <ThemeIcon name="rules" /> Licenses
    </Button>
    <FeedbackPanel {getProfileCredential} />
    {#if latteUrl}
      <Button variant="ghost" full href={latteUrl} target="_blank" rel="noopener noreferrer">
        <ThemeIcon name="coffee" /> Buy Me a Latte
      </Button>
    {:else}
      <Button variant="ghost" full disabled title="Donation link coming soon">
        <ThemeIcon name="coffee" /> Buy Me a Latte
      </Button>
    {/if}
    <Button variant="destructive" class="clear-user-data" onclick={clearUserData}>
      <ThemeIcon name="trash" monochrome /> Clear User Data
    </Button>
  </Card>
</section>
<LicensesDialog open={licensesOpen} close={() => (licensesOpen = false)} />

<style>
  #screen-settings {
    width: 100%;
    margin: 0 auto;
  }
  :global(.settings-actions .btn) {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    text-align: start;
    gap: 12px;
  }
  :global(.settings-actions .btn > .theme-icon) {
    width: 24px;
    flex: 0 0 24px;
  }
  :global(.clear-user-data) {
    margin-top: 24px;
  }
</style>
